import { TranslationService } from '../../../services/translation.service';
import {
	DatacenterDateFilter,
	FilterType,
	DatacenterFreeTextFilter,
	SuggestiveListFilter,
	DatacenterNumericFilter,
	OrderNumberFilter,
	FilterApiRequest,
	DropdownFilter,
	GlobalSearchFilter,
	DatacenterBooleanFilter,
	SuggestiveListWithIdFilter
} from '../../../models/datacenter-filters';
import { ORDER_SERVER_URL } from '../../../../config';

const GET_DATACENTER_BATCHES_URL_V5 = `${ ORDER_SERVER_URL }/v5/datacenterBatches`;
const GET_DATACENTER_ORDERS_URL = `${ ORDER_SERVER_URL }/v4/dataCenterOrders`;

type FilterTypeFn = () => FilterType;

export class BatchFiltersConfiguration {
	constructor(private readonly translationService: TranslationService) {}

	// eslint-disable-next-line @typescript-eslint/naming-convention, max-lines-per-function
	BATCHES_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'steelgrade',
				() => new SuggestiveListFilter(
					'steelgrade',
					this.translationService.getTR('order-tracking.steelgrade'),
					this.translationService.getTR('order-tracking.steelgrade'),
					'steelgrade',
					'steelgradeDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'batchReference',
				() => new DatacenterFreeTextFilter(
					'batchReference',
					this.translationService.getTR('datacenter-filter.batch-reference'),
					this.translationService.getTR('datacenter-filter.batch-reference'),
					'batchReference'
				)
			],
			[
				'currentLocation',
				() => new DatacenterFreeTextFilter(
					'currentLocation',
					this.translationService.getTR('datacenter-filter.current-location'),
					this.translationService.getTR('datacenter-filter.current-location'),
					'currentLocationDescription'
				)
			],
			[
				'readyToShipDate',
				() => new DatacenterDateFilter(
					'readyToShipDate',
					this.translationService.getTR('batch.date-for-dispatch'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'readyToShipDate',
					'date'
				)
			],
			[
				'daysInStock',
				() => new DatacenterNumericFilter(
					'daysInStock',
					this.translationService.getTR('order.daysInStock'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'daysInStock'
				)
			],
			[
				'customerOrderNumber',
				() => new OrderNumberFilter(
					'customerOrderNumber',
					this.translationService.getTR('order.customer.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'customerOrderNumber',
					'customerOrderItemNumber'
				)
			],
			[
				'supplierOrderNumber',
				() => new OrderNumberFilter(
					'supplierOrderNumber',
					this.translationService.getTR('order.supplier.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'supplierOrderNumber',
					'supplierOrderItemNumber'
				)
			],
			[
				'statusGroup',
				() => new DatacenterBooleanFilter(
					'statusGroup',
					this.translationService.getTR('batch.status'),
					[
						this.translationService.getTR('status-group.in-prod'),
						this.translationService.getTR('status-group.blocked'),
						this.translationService.getTR('status-group.rtt'),
						this.translationService.getTR('status-group.in-transit'),
						this.translationService.getTR('status-group.in-consignment'),
						this.translationService.getTR('filter.delivered')
					],
					['statusGroup'],
					['0', '1', '2', '3', '4', '5']
				)
			],
			[
				'supplierOrderType',
				() =>
					new DropdownFilter(
						'supplierOrderType',
						this.translationService.getTR('datacenter-filter.spot-or-schedule'),
						this.translationService.getTR('datacenter-filter.spot-or-schedule'),
						'supplierOrderType',
						'supplierOrderType',
						new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500 }),
						[],
						undefined,
						new Map<string, string[]>([
							['spot', ['C']],
							['scheduled', ['E', 'F']],
						]),
						false,
						undefined,
						false,
						true,
					),
			],
			[
				'choice',
				() => new DropdownFilter(
					'choice',
					this.translationService.getTR('datacenter-filter.choice'),
					this.translationService.getTR('datacenter-filter.choice'),
					'choice',
					'choiceDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'form',
				() => new DropdownFilter(
					'form',
					this.translationService.getTR('datacenter-filter.form'),
					this.translationService.getTR('datacenter-filter.form'),
					'form',
					'formDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'productDescription',
				() => new DropdownFilter(
					'productDescription',
					this.translationService.getTR('datacenter-filter.product-description'),
					this.translationService.getTR('datacenter-filter.product-description'),
					'productDescription',
					'productDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'orderItemStatus',
				() => new DatacenterBooleanFilter(
					'orderItemStatus',
					this.translationService.getTR('datacenter-filter.order-item-status'),
					[
						this.translationService.getTR('global.open'),
						this.translationService.getTR('global.closed'),
						this.translationService.getTR('global.cancelled'),
						this.translationService.getTR('order-item-status.completely-produced')
					],
					['orderItemStatus'],
					['01', '02', '03', '05']
				)
			],
			[
				'customerArticleReference',
				() => new SuggestiveListWithIdFilter(
					'customerArticleReference',
					this.translationService.getTR('commercial-documents.customer-article-reference'),
					this.translationService.getTR('commercial-documents.customer-article-reference'),
					'customerArticleReference',
					'customerArticleReference',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true }),
					[],
					undefined,
					this.translationService.getTR('filter.no-value')
				),
			],
			[
				'cdd',
				() =>
					new DatacenterDateFilter(
						'cdd',
						this.translationService.getTR('datacenter-filter.cdd'),
						this.translationService.getTR('filter.date.from'),
						this.translationService.getTR('filter.date.to'),
						'cddWeek',
						'week',
					),
			],
			[
				'deliveryModeDescription',
				() => new DropdownFilter(
					'deliveryModeDescription',
					this.translationService.getTR('filter.deliveryMode'),
					this.translationService.getTR('filter.deliveryMode'),
					'deliveryMode',
					'deliveryModeDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500 })
				)
			],
			[
				'transportModeDescription',
				() => new DropdownFilter(
					'transportModeDescription',
					this.translationService.getTR('datacenter-filter.transport-mode'),
					this.translationService.getTR('datacenter-filter.transport-mode'),
					'transportMode',
					'transportModeDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500 })
				)
			],
			[
				'carrierName',
				() => new DropdownFilter(
					'carrierName',
					this.translationService.getTR('datacenter-filter.carrier-name'),
					this.translationService.getTR('datacenter-filter.carrier-name'),
					'carrierName',
					'carrierName',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'millOrderNumber',
				() => new OrderNumberFilter(
					'millOrderNumber',
					this.translationService.getTR('datacenter-filter.mill-order-number'),
					this.translationService.getTR('filter.order-number.reference'),
					this.translationService.getTR('filter.order-number.item'),
					'millOrderNumber',
					'millOrderItemNumber'
				)
			],
			[
				'dispatchAdviceNumber',
				() =>
					new DatacenterFreeTextFilter(
						'dispatchAdviceNumber',
						this.translationService.getTR('datacenter-filter.dispatch-advice-number'),
						this.translationService.getTR('datacenter-filter.dispatch-advice-number'),
						'lastDispatchAdviceNumber',
					),
			],
			[
				'statusCategory',
				() =>
					new DropdownFilter(
						'statusCategory',
						this.translationService.getTR('batch.in-transit-detail'),
						this.translationService.getTR('batch.in-transit-detail'),
						'batchStatusCategory',
						'batchStatusCategoryDescription',
						new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true }),
					),
			],
			[
				'lastDispatchAdviceDate',
				() => new DatacenterDateFilter(
					'lastDispatchAdviceDate',
					this.translationService.getTR('datacenter-filter.dispatch-advice-date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'lastDispatchAdviceDate',
					'date'
				)
			],
			[
				'batchStatus',
				() => new DropdownFilter(
					'batchStatus',
					this.translationService.getTR('datacenter-filter.detailed-batch-status'),
					this.translationService.getTR('datacenter-filter.detailed-batch-status'),
					'status',
					'statusDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'soldTo',
				() => new SuggestiveListFilter(
					'soldTo',
					this.translationService.getTR('order.customer'),
					this.translationService.getTR('order.customer'),
					'soldTo',
					'soldToDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'mill',
				() => new SuggestiveListFilter(
					'mill',
					this.translationService.getTR('order.mill'),
					this.translationService.getTR('order.mill'),
					'mill',
					'millDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'shipTo',
				() => new SuggestiveListWithIdFilter(
					'shipTo',
					this.translationService.getTR('order.ship.to'),
					this.translationService.getTR('order.ship.to'),
					'shipTo',
					'shipToDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true }),
					undefined,
					'shipToAddress'
				)
			],
			[
				'coating',
				() =>
					new DropdownFilter(
						'coating',
						this.translationService.getTR('order.coating'),
						this.translationService.getTR('order.coating'),
						'coating',
						'coatingDescription',
						new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 }),
					),
			],
			[
				'serviceLevelDescription',
				() =>
					new DropdownFilter(
						'serviceLevelDescription',
						this.translationService.getTR('datacenter-filter.service-level'),
						this.translationService.getTR('datacenter-filter.service-level'),
						'serviceLevel',
						'serviceLevelDescription',
						new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 }),
					),
			],
			[
				'heatNumber',
				() =>
					new DatacenterFreeTextFilter(
						'heatNumber',
						this.translationService.getTR('datacenter-filter.heat-number'),
						this.translationService.getTR('datacenter-filter.heat-number'),
						'heatNumber',
					),
			],
			[
				'loadingProgramNumber',
				() =>
					new DatacenterFreeTextFilter(
						'loadingProgramNumber',
						this.translationService.getTR('datacenter-filter.loading-program-number'),
						this.translationService.getTR('datacenter-filter.loading-program-number'),
						'loadingProgramNumber',
					),
			],
			[
				'transportReference',
				() =>
					new DatacenterFreeTextFilter(
						'transportReference',
						this.translationService.getTR('datacenter-filter.transport-reference'),
						this.translationService.getTR('datacenter-filter.transport-reference'),
						'transportReference',
					),
			],
			[
				'endUser',
				() =>
					new SuggestiveListFilter(
						'endUser',
						this.translationService.getTR('order.end.user'),
						this.translationService.getTR('order.end.user'),
						'endUser',
						'endUserDescription',
						new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true }),
					),
			],
			[
				'length',
				() =>
					new DatacenterNumericFilter(
						'length',
						this.translationService.getTR('batch-card.length'),
						this.translationService.getTR('filter.number.from'),
						this.translationService.getTR('filter.number.to'),
						'length',
					),
			],
			[
				'width',
				() =>
					new DatacenterNumericFilter(
						'width',
						this.translationService.getTR('filter.width'),
						this.translationService.getTR('filter.number.from'),
						this.translationService.getTR('filter.number.to'),
						'width',
					),
			],
			[
				'thickness',
				() =>
					new DatacenterNumericFilter(
						'thickness',
						this.translationService.getTR('filter.thickness'),
						this.translationService.getTR('filter.number.from'),
						this.translationService.getTR('filter.number.to'),
						'thickness',
					),
			],
			[
				'supplierInvoiceNumber',
				() =>
					new DatacenterFreeTextFilter(
						'supplierInvoiceNumber',
						this.translationService.getTR('batch.supplier.invoice.number'),
						this.translationService.getTR('batch.supplier.invoice.number'),
						'supplierInvoiceNumber',
					),
			],
			[
				'shipToAddress',
				() =>
					new DatacenterFreeTextFilter(
						'shipToAddress',
						this.translationService.getTR('datacenter-filter.destination-address'),
						this.translationService.getTR('datacenter-filter.destination-address'),
						'shipToAddress',
					),
			],
			[
				'invoiceDate',
				() =>
					new DatacenterDateFilter(
						'invoiceDate',
						this.translationService.getTR('batch.invoice.date'),
						this.translationService.getTR('filter.date.from'),
						this.translationService.getTR('filter.date.to'),
						'invoiceDate',
						'date',
					),
			],
			[
				'colorBottomSide',
				() => new DropdownFilter(
					'colorBottomSide',
					this.translationService.getTR('datacenter-filter.color-bottom-side'),
					this.translationService.getTR('datacenter-filter.color-bottom-side'),
					'colorBottomSide',
					'colorBottomSideDescription',
					new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true })
				)
			],
			[
				'colorTopSide',
				() =>
					new DropdownFilter(
						'colorTopSide',
						this.translationService.getTR('datacenter-filter.color-top-side'),
						this.translationService.getTR('datacenter-filter.color-top-side'),
						'colorTopSide',
						'colorTopSideDescription',
						new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true }),
					),
			],
			[
				'globalSearch',
				() =>
					new GlobalSearchFilter(
						'globalSearch',
						this.translationService.getTR('global.search-filter'),
						this.translationService.getTR('global.search-filter'),
						['batchReference', 'supplierOrderNumber', 'customerOrderNumber'],
					),
			],
			[
				'shipToAddress',
				() =>
					new DatacenterFreeTextFilter(
						'shipToAddress',
						this.translationService.getTR('datacenter-filter.destination-address'),
						this.translationService.getTR('datacenter-filter.destination-address'),
						'shipToAddress',
					),
			],
			[
				'shipmentReferenceDate',
				() =>
					new DatacenterDateFilter(
						'shipmentReferenceDate',
						this.translationService.getTR('shipment.dispatch-date'),
						this.translationService.getTR('filter.date.from'),
						this.translationService.getTR('filter.date.to'),
						'shipmentReferenceDate',
						'date',
					),
			],
			[
				'salesQuarter',
				() => new DatacenterDateFilter(
					'salesQuarter',
					this.translationService.getTR('order.sales.quarter'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'salesQuarter',
					'quarter'
				)
			],
			[
				'destinationByBoat',
				() =>
					new DatacenterFreeTextFilter(
						'destinationByBoat',
						this.translationService.getTR('datacenter-filter.destination-by-boat'),
						this.translationService.getTR('datacenter-filter.destination-by-boat'),
						'destinationByBoat',
					),
			],
			[
				'productCategorySTU',
				() =>
					new DropdownFilter(
						'productCategorySTU',
						this.translationService.getTR('datacenter-filter.product-category'),
						this.translationService.getTR('datacenter-filter.product-category'),
						'productCategorySTU',
						'productCategorySTUDescription',
						new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true }),
					),
			],
			[
				'serviceRequest',
				() =>
					new SuggestiveListFilter(
						'serviceRequest',
						this.translationService.getTR('datacenter-filter.service-request'),
						this.translationService.getTR('datacenter-filter.service-request'),
						'serviceRequest',
						'serviceRequestDescription',
						new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true }),
					),
			],
			[
				'rddWeek',
				() =>
					new DatacenterDateFilter(
						'rddWeek',
						this.translationService.getTR('datacenter-filter.rdd-week'),
						this.translationService.getTR('filter.date.from'),
						this.translationService.getTR('filter.date.to'),
						'rddWeek',
						'week',
					),
			],
			[
				'producedLength',
				() =>
					new DatacenterNumericFilter(
						'producedLength',
						this.translationService.getTR('datacenter-filter.producedLength'),
						this.translationService.getTR('filter.number.from'),
						this.translationService.getTR('filter.number.to'),
						'producedLength',
					),
			],
			[
				'producedWidth',
				() =>
					new DatacenterNumericFilter(
						'producedWidth',
						this.translationService.getTR('datacenter-filter.producedWidth'),
						this.translationService.getTR('filter.number.from'),
						this.translationService.getTR('filter.number.to'),
						'producedWidth',
					),
			],
			[
				'producedThickness',
				() =>
					new DatacenterNumericFilter(
						'producedThickness',
						this.translationService.getTR('datacenter-filter.producedThickness'),
						this.translationService.getTR('filter.number.from'),
						this.translationService.getTR('filter.number.to'),
						'producedThickness',
					),
			],
			[
				'stockAlert',
				() =>
					new DropdownFilter(
						'stockAlert',
						this.translationService.getTR('order.stock-alert'),
						this.translationService.getTR('order.stock-alert'),
						'stockAlert',
						'stockAlertDescription',
						new FilterApiRequest(GET_DATACENTER_ORDERS_URL, { limit: 1500 }),
						undefined,
						undefined,
						undefined,
						false,
						undefined,
						false,
						true,
					),
			],
			[
				'rowCreationDate',
				() => new DatacenterDateFilter(
					'rowCreationDate',
					this.translationService.getTR('ecall.row.creation.date'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'rowCreationDate',
					'date'
				)
			],
			[
				'customerReference',
				() => new DatacenterFreeTextFilter(
					'customerReference',
					this.translationService.getTR('ecall-off.customer-call-off-reference'),
					this.translationService.getTR('ecall-off.customer-call-off-reference'),
					'customerReference'
				)
			],
			[
				'ecallMillBatchReference',
				() => new DatacenterFreeTextFilter(
					'ecallMillBatchReference',
					this.translationService.getTR('ecall-off.supplier-call-off-reference'),
					this.translationService.getTR('ecall-off.supplier-call-off-reference'),
					'ecallMillBatchReference'
				)
			],
			[
				'callCreator',
				() => new DatacenterFreeTextFilter(
					'callCreator',
					this.translationService.getTR('ecall.batch.callCreator'),
					this.translationService.getTR('ecall.batch.callCreator'),
					'callCreator'
				)
			],
			[
				'ecallStatus',
				() =>
					new DropdownFilter(
						'ecallStatus',
						this.translationService.getTR('batch.ecall-status'),
						this.translationService.getTR('batch.ecall-status'),
						'ecallStatus',
						'ecallStatus',
						new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500 }),
						[],
						undefined,
						undefined,
						false,
						undefined,
						false,
						true,
					),
			],
			[
				'ecallShipToDescription',
				() =>
					new DropdownFilter(
						'ecallShipToDescription',
						this.translationService.getTR('ecall-off.requested-ship-to'),
						this.translationService.getTR('ecall-off.requested-ship-to'),
						'ecallShipToDescription',
						'ecallShipToDescription',
						new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500 }),
						[],
						undefined,
						undefined,
						false,
						undefined,
						false,
						true,
					),
			],
			[
				'requestedDeliveryDateFrom',
				() => new DatacenterDateFilter(
					'requestedDeliveryDateFrom',
					this.translationService.getTR('batch.requested-delivery-date-from'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'requestedDeliveryDateFrom',
					'date'
				)
			],
			[
				'requestedDeliveryDateTo',
				() => new DatacenterDateFilter(
					'requestedDeliveryDateTo',
					this.translationService.getTR('batch.requested-delivery-date-to'),
					this.translationService.getTR('filter.date.from'),
					this.translationService.getTR('filter.date.to'),
					'requestedDeliveryDateTo',
					'date'
				)
			],
			[
				'callOffUnloadingReference',
				() => new DatacenterFreeTextFilter(
					'callOffUnloadingReference',
					this.translationService.getTR('ecall-off.unloading-reference'),
					this.translationService.getTR('ecall-off.unloading-reference'),
					'callOffUnloadingReference'
				)
			],
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	BATCH_DATACENTER_DEFAULT_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'mill',
				() =>
					new SuggestiveListFilter(
						'mill',
						this.translationService.getTR('order.mill'),
						this.translationService.getTR('order.mill'),
						'mill',
						'millDescription',
						new FilterApiRequest(GET_DATACENTER_BATCHES_URL_V5, { limit: 1500, distinct: true }),
						[{}],
					),
			],
			[
				'customerOrderNumber',
				() =>
					new OrderNumberFilter(
						'customerOrderNumber',
						this.translationService.getTR('order.customer.order.number'),
						this.translationService.getTR('filter.order-number.reference'),
						this.translationService.getTR('filter.order-number.item'),
						'customerOrderNumber',
						'customerOrderItemNumber',
						[{}],
					),
			],
			[
				'supplierOrderNumber',
				() =>
					new OrderNumberFilter(
						'supplierOrderNumber',
						this.translationService.getTR('order.supplier.order.number'),
						this.translationService.getTR('filter.order-number.reference'),
						this.translationService.getTR('filter.order-number.item'),
						'supplierOrderNumber',
						'supplierOrderItemNumber',
						[{}],
					),
			],
			[
				'batchReference',
				() =>
					new DatacenterFreeTextFilter(
						'batchReference',
						this.translationService.getTR('datacenter-filter.batch-reference'),
						this.translationService.getTR('datacenter-filter.batch-reference'),
						'batchReference',
						[''],
					),
			],
			[
				'daysInStock',
				() =>
					new DatacenterNumericFilter(
						'daysInStock',
						this.translationService.getTR('order.daysInStock'),
						this.translationService.getTR('filter.number.from'),
						this.translationService.getTR('filter.number.to'),
						'daysInStock',
						[{ from: '', to: '' }],
					),
			],
			[
				'lastDispatchAdviceDate',
				() =>
					new DatacenterDateFilter(
						'lastDispatchAdviceDate',
						this.translationService.getTR('datacenter-filter.dispatch-advice-date'),
						this.translationService.getTR('filter.date.from'),
						this.translationService.getTR('filter.date.to'),
						'lastDispatchAdviceDate',
						'date',
						[
							{
								from: '',
								to: '',
							},
						],
					),
			],
		]);
}
